import { useEffect, useState, type ReactElement } from 'react';
import { Spinner } from '@csa/ui';
import { useAuth } from './auth/AuthProvider.js';
import { useSession } from './auth/useSession.js';
import { SignInScreen } from './auth/SignInScreen.js';
import { SignedOutScreen } from './auth/SignedOutScreen.js';
import { FatalError } from './components/FatalError.js';
import { ApiError } from './lib/api.js';
import { AccessPending } from './components/AccessPending.js';
import { ConsoleShell } from './shell/ConsoleShell.js';

/**
 * The authentication gate.
 *
 * Nothing below this component renders until both halves of identity are
 * settled: Entra has issued a token, and the server has confirmed the user is
 * provisioned and enabled. Rendering the shell optimistically and letting
 * individual panels 401 would show an authenticated-looking interface to
 * somebody whose access has been revoked.
 */
export function App(): ReactElement {
  const { status, user, error, signOut } = useAuth();
  const path = useLocationPath();

  // The redirect lands here with the authorization code still in the URL.
  // MSAL consumes it during initialisation, so this is a hold screen; showing
  // the sign-in button instead would invite a second, failing login.
  if (path === '/auth/callback' || status === 'initialising') {
    return <FullPageSpinner label="Completing sign-in" />;
  }

  if (path === '/signed-out') {
    return <SignedOutScreen />;
  }

  if (status === 'error') {
    return (
      <FatalError
        title="Sign-in failed"
        message={error ?? 'Microsoft Entra ID did not complete the sign-in.'}
      />
    );
  }

  if (status === 'signed-out' || !user) {
    return <SignInScreen />;
  }

  return <AuthenticatedApp onSignOut={signOut} path={path} />;
}

function AuthenticatedApp({
  onSignOut,
  path,
}: {
  onSignOut: () => void;
  path: string;
}): ReactElement {
  const session = useSession(true);

  if (session.isPending) {
    return <FullPageSpinner label="Loading your access" />;
  }

  if (session.error) {
    const err = session.error;

    // A disabled account is the expected, designed outcome of an administrator
    // revoking access, not a failure. It gets its own screen so the user is
    // told what happened rather than shown a stack of retry buttons.
    if (err instanceof ApiError && err.isForbidden) {
      return <AccessPending reason={err.message} onSignOut={onSignOut} variant="disabled" />;
    }

    return (
      <FatalError title="Could not load your access" message={err.message}>
        <button
          type="button"
          onClick={onSignOut}
          className="text-sm font-medium text-brand underline-offset-2 hover:underline"
        >
          Sign out
        </button>
      </FatalError>
    );
  }

  // Authenticated, provisioned, holding nothing. This is the designed state of
  // a brand-new account, not a failure: authentication grants no
  // authorisation. Rendering an empty shell instead would just look broken.
  const { entitlements, isBootstrapAdmin } = session.data;
  if (entitlements.all.length === 0 && !isBootstrapAdmin) {
    return (
      <AccessPending
        variant="pending"
        reason="Your account exists but no roles are assigned to it."
        onSignOut={onSignOut}
      />
    );
  }

  return <ConsoleShell session={session.data} currentPath={path} onSignOut={onSignOut} />;
}

function FullPageSpinner({ label }: { label: string }): ReactElement {
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-4 bg-sunken"
      role="status"
      aria-live="polite"
    >
      <Spinner className="size-6 text-brand" label={null} />
      <p className="text-sm text-muted">{label}…</p>
    </div>
  );
}

/**
 * Minimal location tracking.
 *
 * The router is introduced with the consoles; until then the shell only needs
 * to know the current path, and reading it here avoids having the auth gate
 * depend on a router that has to render inside it anyway.
 */
function useLocationPath(): string {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const onChange = (): void => setPath(window.location.pathname);
    window.addEventListener('popstate', onChange);
    return () => window.removeEventListener('popstate', onChange);
  }, []);

  return path;
}
